import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { profileApi } from '@internal/api/profile';
import { isUserProjectFinished } from '@internal/components/experience/utils/project-finish-deps';
import { useLoadedOnce } from '@internal/hooks/use-loaded-once';
import { Education, UserProject } from '@internal/types/experience';
import { Profile } from '@internal/types/profile';

import { MIN_PROJECTS_COUNT } from '../experience-highlights-section';
import { ProfileSectionId } from '../user-profile.interfaces';
import {
  isEducationFinished,
  isProfileFieldMissing,
  missingEducationTargetId,
  missingFieldTargetId,
  missingProjectTargetId,
  PROFILE_FIELD_ORDER,
} from './missing-fields.constants';

interface UseProfileMissingFieldsParams {
  profile: Profile;
  projects: UserProject[];
  projectsIsLoading: boolean;
  educations: Education[];
  educationsIsLoading: boolean;
  activeSectionId: ProfileSectionId;
  onSectionChange: (sectionId: ProfileSectionId) => void;
}

interface MissingTarget {
  id: string;
  sectionId: ProfileSectionId;
}

export interface ProfileMissingFieldsResult {
  missingCount: number;
  currentPosition: number;
  disablePrev: boolean;
  disableNext: boolean;
  stepPrev: () => void;
  stepNext: () => void;
  highlightedId: string | null;
}

export const useProfileMissingFields = ({
  profile,
  projects,
  projectsIsLoading,
  educations,
  educationsIsLoading,
  activeSectionId,
  onSectionChange,
}: UseProfileMissingFieldsParams): ProfileMissingFieldsResult => {
  const { skills = [], skillsIsLoading } = profileApi.getSkills.useQuery(
    profile.apiKey ?? '',
    { enabled: Boolean(profile.apiKey) }
  );
  const skillsPending = !useLoadedOnce(skillsIsLoading);
  const targets = useMemo<MissingTarget[]>(() => {
    const context = {
      profile,
      projects,
      projectsIsLoading,
      educations,
      educationsIsLoading,
      skills,
      skillsIsLoading: skillsPending,
    };
    const list: MissingTarget[] = [];

    PROFILE_FIELD_ORDER.forEach(def => {
      if (def.key === 'experience') {
        if (projectsIsLoading) return;
        const unfinished = projects.filter(
          project => !isUserProjectFinished(project)
        );
        unfinished.forEach(project =>
          list.push({
            id: missingProjectTargetId(project.id),
            sectionId: def.sectionId,
          })
        );
        if (unfinished.length === 0 && projects.length < MIN_PROJECTS_COUNT) {
          list.push({
            id: missingFieldTargetId('experience'),
            sectionId: def.sectionId,
          });
        }
        return;
      }

      if (def.key === 'education') {
        if (educationsIsLoading) return;
        const unfinished = educations.filter(
          education => !isEducationFinished(education)
        );
        unfinished.forEach(education =>
          list.push({
            id: missingEducationTargetId(education.id),
            sectionId: def.sectionId,
          })
        );

        if (
          unfinished.length === 0 &&
          !educations.some(education => isEducationFinished(education))
        ) {
          list.push({
            id: missingFieldTargetId('education'),
            sectionId: def.sectionId,
          });
        }
        return;
      }

      if (isProfileFieldMissing(def.key, context)) {
        list.push({
          id: missingFieldTargetId(def.key),
          sectionId: def.sectionId,
        });
      }
    });

    return list;
  }, [
    profile,
    projects,
    projectsIsLoading,
    educations,
    educationsIsLoading,
    skills,
    skillsPending,
  ]);

  const [currentId, setCurrentId] = useState<string | null>(null);
  const lastTargetsWithCurrentRef = useRef<MissingTarget[]>([]);

  const currentIndex = useMemo(() => {
    if (!targets.length || !currentId) return 0;
    const index = targets.findIndex(target => target.id === currentId);
    if (index !== -1) return index;

    const previous = lastTargetsWithCurrentRef.current;
    const follower = previous
      .slice(previous.findIndex(target => target.id === currentId) + 1)
      .find(candidate => targets.some(target => target.id === candidate.id));
    return follower
      ? targets.findIndex(target => target.id === follower.id)
      : targets.length - 1;
  }, [currentId, targets]);

  useEffect(() => {
    if (currentId && targets.some(target => target.id === currentId)) {
      lastTargetsWithCurrentRef.current = targets;
    }
  }, [currentId, targets]);

  const goTo = useCallback(
    (target: MissingTarget) => {
      // The anchor may not be painted yet right after a tab switch (or while
      // a section is still mounting) — retry across a few frames.
      const scrollToTarget = (attempt = 0) => {
        const element = document.getElementById(target.id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (attempt < 10) {
          requestAnimationFrame(() => scrollToTarget(attempt + 1));
        }
      };

      if (target.sectionId !== activeSectionId) {
        onSectionChange(target.sectionId);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => scrollToTarget())
        );
        return;
      }

      scrollToTarget();
    },
    [activeSectionId, onSectionChange]
  );

  const step = useCallback(
    (delta: number) => {
      if (!targets.length) return;

      let nextIndex = currentIndex;
      if (targets.some(target => target.id === currentId)) {
        nextIndex += delta;
      } else if (currentId && delta < 0) {
        nextIndex -= 1;
      }
      nextIndex = Math.min(targets.length - 1, Math.max(0, nextIndex));

      setCurrentId(targets[nextIndex].id);
      goTo(targets[nextIndex]);
    },
    [currentId, currentIndex, targets, goTo]
  );

  return {
    missingCount: targets.length,
    currentPosition: currentIndex + 1,
    disablePrev: targets.length === 0,
    disableNext: targets.length === 0,
    stepPrev: () => step(-1),
    stepNext: () => step(1),
    highlightedId: currentId ? targets[currentIndex]?.id ?? null : null,
  };
};
